/**
 * 配對分析頁面
 * 允許使用者輸入兩個人的命盤資料進行配對分析
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  SelectChangeEvent,
  Snackbar,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CompareArrows as CompareArrowsIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../contexts/CurrencyContext';
import { analyzePair, validatePairBirthTime, PairRequest, relationshipTypeOptions, RelationshipType } from '../api/pair';
import { checkBalanceSufficient } from '../api/qa';
import apiClient from '../api/client';
import { createInvite } from '../api/invite';

/**
 * 13 時辰選項（子時分為凌晨/深夜兩項）
 */
const hourOptions = [
  { value: '23_early', label: '子時 (00:01-01:00)' },
  { value: '01', label: '丑時 (01:01-03:00)' },
  { value: '03', label: '寅時 (03:01-05:00)' },
  { value: '05', label: '卯時 (05:01-07:00)' },
  { value: '07', label: '辰時 (07:01-09:00)' },
  { value: '09', label: '巳時 (09:01-11:00)' },
  { value: '11', label: '午時 (11:01-13:00)' },
  { value: '13', label: '未時 (13:01-15:00)' },
  { value: '15', label: '申時 (15:01-17:00)' },
  { value: '17', label: '酉時 (17:01-19:00)' },
  { value: '19', label: '戌時 (19:01-21:00)' },
  { value: '21', label: '亥時 (21:01-23:00)' },
  { value: '23_late', label: '子時 (23:01-24:00)' },
];

/** 將前端時辰值轉換為後端 hh 值 */
const toHourValue = (v: string) => (v === '23_early' || v === '23_late') ? '23' : v;

/** 組合 birth_time (YYYYMMDDhh)，23_early 自動將日期 -1 天 */
const buildBirthTime = (year: string, month: string, day: string, hour: string): string => {
  let y = parseInt(year, 10);
  let m = parseInt(month, 10);
  let d = parseInt(day, 10);
  if (hour === '23_early') {
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() - 1);
    y = date.getFullYear();
    m = date.getMonth() + 1;
    d = date.getDate();
  }
  return `${y}${m.toString().padStart(2, '0')}${d.toString().padStart(2, '0')}${toHourValue(hour)}`;
};

const PairAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();

  // 從路由 state 或 sessionStorage 接收問題資訊
  const getInitialQuestionData = () => {
    // 優先使用 location.state
    if (location.state?.question_id) {
      return {
        question_id: location.state.question_id,
        question_text: location.state.question_text,
        price: location.state.price
      };
    }
    // 其次嘗試 sessionStorage
    try {
      const stored = sessionStorage.getItem('pairAnalysisQuestion');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to parse stored question:', e);
    }
    return null;
  };

  // 使用 useState 初始化函數，只在首次渲染時執行一次
  const [initialData] = useState(() => getInitialQuestionData());

  // 問題資訊
  const [questionId] = useState<string>(initialData?.question_id || '');
  const [questionText] = useState<string>(initialData?.question_text || '配對分析');
  const [questionPrice] = useState<number>(initialData?.price || 199);

  // 在 useEffect 中清除 sessionStorage（只執行一次）
  useEffect(() => {
    sessionStorage.removeItem('pairAnalysisQuestion');
  }, []);

  // 從分享命盤跳轉時，預填 Person B
  const [personBFromShare, setPersonBFromShare] = useState<string | null>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);
  useEffect(() => {
    const raw = sessionStorage.getItem('pair_from_share');
    if (raw) {
      try {
        const d = JSON.parse(raw);
        if (d.year)   setPersonBYear(d.year);
        if (d.month)  setPersonBMonth(d.month);
        if (d.day)    setPersonBDay(d.day);
        if (d.hour)   setPersonBHour(d.hour);
        if (d.gender === 'Male' || d.gender === 'Female') setPersonBGender(d.gender);
        if (d.name)   setPersonBName(d.name);
        if (d.share_token) setShareToken(d.share_token);
        setPersonBFromShare(d.name || '分享者');
      } catch {}
      sessionStorage.removeItem('pair_from_share');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Person A 資料
  const [personAName, setPersonAName] = useState<string>('');
  const [personAYear, setPersonAYear] = useState<string>('');
  const [personAMonth, setPersonAMonth] = useState<string>('');
  const [personADay, setPersonADay] = useState<string>('');
  const [personAHour, setPersonAHour] = useState<string>('');
  const [personAGender, setPersonAGender] = useState<'Male' | 'Female'>('Male');

  // Person B 資料（單人配對模式）
  const [personBName, setPersonBName] = useState<string>('');
  const [personBYear, setPersonBYear] = useState<string>('');
  const [personBMonth, setPersonBMonth] = useState<string>('');
  const [personBDay, setPersonBDay] = useState<string>('');
  const [personBHour, setPersonBHour] = useState<string>('');
  const [personBGender, setPersonBGender] = useState<'Male' | 'Female'>('Female');

  // 多人配對資料（工作夥伴模式）
  interface PersonData {
    id: number;
    name: string;
    year: string;
    month: string;
    day: string;
    hour: string;
    gender: 'Male' | 'Female';
  }

  const [multiplePersons, setMultiplePersons] = useState<PersonData[]>([
    { id: 1, name: '', year: '', month: '', day: '', hour: '', gender: 'Female' }
  ]);

  // 關係類型（僅當沒有指定 questionId 時使用）
  const [relationshipType, setRelationshipType] = useState<RelationshipType>('romantic');

  /**
   * 判斷是否為雙人配對類型
   * PAIR001-PAIR025: 雙人配對
   * PAIR026-PAIR034: 多人配對
   * SIMPLE001: 簡易版配對（視為雙人配對）
   */
  const isPairQuestion = (qid: string): boolean => {
    if (!qid) return false;
    // SIMPLE001 視為雙人配對
    if (qid === 'SIMPLE001') return true;
    // MAIN009 (感情走勢) 需要雙人資料，視為雙人配對
    if (qid === 'MAIN009') return true;
    // PAIR001-PAIR025 為雙人配對
    if (qid.startsWith('PAIR')) {
      const num = parseInt(qid.replace('PAIR', ''));
      return num >= 1 && num <= 25;
    }
    return false;
  };

  const isMultiPairQuestion = (qid: string): boolean => {
    if (!qid) return false;
    // PAIR026-PAIR034 為多人配對
    if (qid.startsWith('PAIR')) {
      const num = parseInt(qid.replace('PAIR', ''));
      return num >= 26 && num <= 34;
    }
    return false;
  };

  // 判斷配對模式
  const isSinglePairType = questionId ? isPairQuestion(questionId) : ['romantic', 'marriage', 'friendship'].includes(relationshipType);
  const isMultiPairType = questionId ? isMultiPairQuestion(questionId) : relationshipType === 'business';

  // 配對費用
  const pairCost = questionPrice || 199;

  // UI 狀態
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  // 邀請連結狀態
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [copySnackbar, setCopySnackbar] = useState(false);

  /**
   * 使用當前使用者資料填充 Person A
   */
  useEffect(() => {
    if (user && user.birth_time && user.birth_time.length === 10) {
      setPersonAYear(user.birth_time.substring(0, 4));
      setPersonAMonth(user.birth_time.substring(4, 6));
      setPersonADay(user.birth_time.substring(6, 8));
      setPersonAHour(user.birth_time.substring(8, 10));
      setPersonAGender(user.gender as 'Male' | 'Female');
      setPersonAName(user.full_name || '本人');
    }
  }, [user]);

  /**
   * 添加更多人（工作夥伴模式）
   */
  const handleAddPerson = () => {
    if (multiplePersons.length < 5) {
      const newId = Math.max(...multiplePersons.map(p => p.id)) + 1;
      setMultiplePersons([...multiplePersons, {
        id: newId,
        name: '',
        year: '',
        month: '',
        day: '',
        hour: '',
        gender: 'Female'
      }]);
    }
  };

  /**
   * 移除人員（工作夥伴模式）
   */
  const handleRemovePerson = (id: number) => {
    if (multiplePersons.length > 1) {
      setMultiplePersons(multiplePersons.filter(p => p.id !== id));
    }
  };

  /**
   * 更新人員資料（工作夥伴模式）
   */
  const handleUpdatePerson = (id: number, field: keyof PersonData, value: string) => {
    setMultiplePersons(multiplePersons.map(p =>
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  /**
   * 驗證表單資料
   */
  const validateForm = (): { valid: boolean; message: string } => {
    // Person A 驗證
    if (!personAYear || !personAMonth || !personADay || !personAHour) {
      return { valid: false, message: 'Person A 的出生時間尚未完整填寫' };
    }

    const birthTimeA = buildBirthTime(personAYear, personAMonth, personADay, personAHour);
    if (!validatePairBirthTime(birthTimeA)) {
      return { valid: false, message: 'Person A 的出生時間格式不正確' };
    }

    // 單人配對模式驗證
    if (isSinglePairType) {
      if (!personBYear || !personBMonth || !personBDay || !personBHour) {
        return { valid: false, message: 'Person B 的出生時間尚未完整填寫' };
      }

      const birthTimeB = buildBirthTime(personBYear, personBMonth, personBDay, personBHour);
      if (!validatePairBirthTime(birthTimeB)) {
        return { valid: false, message: 'Person B 的出生時間格式不正確' };
      }
    }

    // 多人配對模式驗證
    if (isMultiPairType) {
      if (!multiplePersons || multiplePersons.length === 0) {
        return { valid: false, message: '請至少輸入一位配對對象的資料' };
      }

      for (let i = 0; i < multiplePersons.length; i++) {
        const person = multiplePersons[i];
        if (!person.year || !person.month || !person.day || !person.hour) {
          return { valid: false, message: `夥伴 ${i + 1} 的出生時間尚未完整填寫` };
        }

        const birthTime = buildBirthTime(person.year, person.month, person.day, person.hour);
        if (!validatePairBirthTime(birthTime)) {
          return { valid: false, message: `夥伴 ${i + 1} 的出生時間格式不正確` };
        }
      }
    }

    // 餘額檢查（分享命盤免費配對跳過）
    if (!shareToken) {
      const availableBalance = user?.available_balance ?? user?.balance ?? 0;
      const totalCost = pairCost;
      if (user && !checkBalanceSufficient(availableBalance, totalCost)) {
        return {
          valid: false,
          message: `餘額不足。配對分析需要 ${String(formatPrice(totalCost))}，您的可用額度為 ${String(formatPrice(availableBalance))}。\n（系統允許透支上限 NT$ 500）`
        };
      }
    }

    return { valid: true, message: '' };
  };

  /**
   * 產生邀請連結（讓對方填寫出生資料）
   */
  const handleCreateInvite = async () => {
    if (!personAName.trim()) { setInviteError('請先填寫您的姓名'); return; }
    if (!personAYear || !personAMonth || !personADay || !personAHour) {
      setInviteError('請先填寫您的出生資料');
      return;
    }
    const qid = getQuestionId();
    if (!qid) { setInviteError('請先選擇配對問題'); return; }
    setInviteError('');
    setInviteLoading(true);
    try {
      const birth_time = buildBirthTime(personAYear, personAMonth, personADay, personAHour);
      const res = await createInvite(qid, personAName.trim(), birth_time, personAGender);
      if (res.success && res.invite_url) {
        const fullUrl = `${window.location.origin}${res.invite_url}`;
        setInviteUrl(fullUrl);
        setInviteDialogOpen(true);
      } else {
        setInviteError(res.error || '產生邀請連結失敗');
      }
    } catch (err: any) {
      setInviteError(err.message || '產生邀請連結失敗');
    } finally {
      setInviteLoading(false);
    }
  };

  /**
   * 處理提交 - 打開確認對話框
   */
  const handleSubmit = () => {
    const validation = validateForm();
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setError('');
    setConfirmDialogOpen(true);
  };

  /**
   * 取得問題 ID
   * 優先使用從路由傳來的 questionId，否則根據關係類型選擇
   */
  const getQuestionId = (): string => {
    // 如果已經有 questionId（從問題列表進來的），直接使用
    if (questionId) {
      return questionId;
    }

    // 否則根據關係類型選擇（向後兼容，直接訪問 /pair 的情況）
    switch (relationshipType) {
      case 'romantic':
        return 'PAIR001'; // 我目前交往的對象適合我嗎？
      case 'marriage':
        return 'PAIR002'; // 我們結婚後會幸福嗎？
      case 'friendship':
        return 'PAIR018'; // 這個朋友值得深交嗎？
      case 'business':
        return 'PAIR016'; // 這個人適合當我的工作夥伴嗎？
      default:
        return 'PAIR001';
    }
  };

  /**
   * 確認配對分析
   */
  const handleConfirmAnalysis = async () => {
    console.log('handleConfirmAnalysis called');
    console.log('isSinglePairType:', isSinglePairType);
    console.log('isMultiPairType:', isMultiPairType);
    console.log('questionId:', questionId);

    setConfirmDialogOpen(false);
    setLoading(true);
    setError('');

    try {
      const birthTimeA = buildBirthTime(personAYear, personAMonth, personADay, personAHour);
      const qid = getQuestionId();
      console.log('Using question ID:', qid);
      console.log('birthTimeA:', birthTimeA);

      // 單人配對模式：使用 Person B
      if (isSinglePairType) {
        const birthTimeB = buildBirthTime(personBYear, personBMonth, personBDay, personBHour);
        console.log('birthTimeB:', birthTimeB);

        const request: PairRequest = {
          person_a: {
            birth_time: birthTimeA,
            gender: personAGender,
            name: personAName || undefined,
          },
          person_b: {
            birth_time: birthTimeB,
            gender: personBGender,
            name: personBName || undefined,
          },
          question_id: qid,
          ...(shareToken ? { share_token: shareToken } : {}),
        };
        console.log('Sending pair request:', request);

        const response = await analyzePair(request);
        console.log('Pair analysis response:', response);

        if (response.success) {
          // 導航到結果頁面，傳遞結果資料
          console.log('Navigating to /pair/result');
          navigate('/pair/result', { state: { result: response } });
        } else {
          console.log('Pair analysis failed:', response);
          setError(response.message || response.error || '配對分析失敗');
        }
      } else {
        console.log('isSinglePairType is false, skipping single pair logic');
      }

      // 多人配對模式：一次性發送所有候選人（只扣一次款）
      if (isMultiPairType && multiplePersons && multiplePersons.length > 0) {
        const multiQid = getQuestionId();
        console.log('Multi-pair using question ID:', multiQid);

        // 準備候選人列表
        const candidates = multiplePersons.map((partner, i) => ({
          birth_time: buildBirthTime(partner.year, partner.month, partner.day, partner.hour),
          gender: partner.gender,
          name: partner.name || `夥伴${i + 1}`,
        }));

        const multiRequest: any = {
          person_a: {
            birth_time: birthTimeA,
            gender: personAGender,
            name: personAName || undefined,
          },
          candidates: candidates,
          question_id: multiQid,
        };

        console.log('Sending multi-pair request:', multiRequest);

        try {
          // 使用新的多人配對 API（只扣一次款）
          // 注意：apiClient 的 response interceptor 已經返回 response.data
          // 所以這裡直接得到的是後端返回的資料對象
          const data: any = await apiClient.post('/api/pair/analyze-multi', multiRequest);
          console.log('Multi-pair analysis data:', data);

          // 防禦性檢查
          if (!data) {
            console.error('Response data is undefined or null');
            setError('伺服器回應格式錯誤');
            return;
          }

          if (data.success) {
            // 轉換結果格式
            const results = data.results?.map((r: any) => ({
              partner: r.candidate_name,
              result: {
                success: true,
                output: r.output,
                result: r.result,
                question_id: data.question_id,
                question_text: data.question_text,
                cost: data.cost,
                balance_after: data.balance_after,
              }
            })) || [];

            // 導航到結果頁面，傳遞多個配對結果
            navigate('/pair/result', {
              state: {
                results: results,
                isMultiPair: true,
                personAName: personAName || '本人',
                totalCost: data.cost,
                balance_after: data.balance_after,
              }
            });
          } else {
            console.log('Multi-pair analysis failed:', data);
            setError(data.message || data.error || '多人配對分析失敗');
          }
        } catch (err: any) {
          console.error('Multi-pair error:', err);
          throw err;
        }
      }
    } catch (err: any) {
      console.error('配對分析錯誤:', err);
      if (err.response?.status === 402) {
        setError('餘額不足，請先儲值');
      } else if (err.response?.status === 401) {
        setError('請先登入');
      } else {
        setError(err.response?.data?.message || err.message || '配對分析失敗');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * 取消確認
   */
  const handleCancelAnalysis = () => {
    setConfirmDialogOpen(false);
  };

  /**
   * 關係類型圖標
   */
  const getRelationshipIcon = (type: RelationshipType) => {
    switch (type) {
      case 'romantic':
      case 'marriage':
        return <FavoriteIcon />;
      case 'business':
        return <BusinessIcon />;
      case 'friendship':
        return <PeopleIcon />;
      default:
        return <CompareArrowsIcon />;
    }
  };

  // 確保 formatPrice 函數存在
  if (!formatPrice || typeof formatPrice !== 'function') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          系統初始化失敗，請重新整理頁面
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* 頁面標題 */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          <CompareArrowsIcon sx={{ fontSize: 40, verticalAlign: 'middle', mr: 1 }} />
          配對分析
        </Typography>
        <Typography variant="body1">
          {questionText || '輸入兩個人的命盤資料，分析配對契合度'}
        </Typography>
        {questionId && (
          <Chip
            label={`問題: ${questionId}`}
            sx={{ mt: 2, mr: 1, backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        )}
        <Chip
          label={shareToken ? '費用：免費（分享命盤）' : `費用：${String(formatPrice(pairCost))}`}
          color={shareToken ? 'success' : 'secondary'}
          sx={{ mt: 2, fontWeight: 'bold' }}
        />
      </Paper>

      {/* 錯誤訊息 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* 關係類型選擇（僅當沒有指定 questionId 時顯示） */}
      {!questionId && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            選擇關係類型
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            如果您想使用特定問題進行配對分析，請從問題列表頁面選擇具體問題
          </Alert>
          <Grid container spacing={2}>
            {relationshipTypeOptions && Array.isArray(relationshipTypeOptions) && relationshipTypeOptions.length > 0 && relationshipTypeOptions
              .filter(option => option && typeof option === 'object' && option.value && option.label && option.description)
              .map((option) => (
                <Grid item xs={12} sm={6} md={3} key={option.value}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: relationshipType === option.value ? '2px solid #667eea' : '1px solid #e0e0e0',
                      '&:hover': { boxShadow: 3 },
                    }}
                    onClick={() => setRelationshipType(option.value)}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box sx={{ fontSize: 40, color: 'primary.main' }}>
                        {getRelationshipIcon(option.value)}
                      </Box>
                      <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
                        {String(option.label)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {String(option.description)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Paper>
      )}

      {/* Person A 資料 */}
      {isSinglePairType && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            您的資料（Person A）
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {questionId === 'SIMPLE001' && '分析你和另一個人的整體配合度（簡單描述版）'}
            {questionId !== 'SIMPLE001' && '適配分析：分析您與另一位的適配度'}
          </Alert>
          <Divider sx={{ mb: 2 }} />

          {/* 只讀顯示用戶資料 */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                姓名
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {String(personAName || user?.full_name || '未提供')}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                性別
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {personAGender === 'Male' ? '男' : '女'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                出生時間
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {String(personAYear)}年 {String(personAMonth)}月 {String(personADay)}日 {String(hourOptions.find(h => h.value === personAHour)?.label || '')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Person A 資料輸入（工作夥伴模式） */}
      {isMultiPairType && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            您的資料（Person A）
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            適配分析：分析最多五個人的適配度
          </Alert>
          <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {/* 姓名（可選）*/}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="姓名（可選）"
              value={personAName}
              onChange={(e) => setPersonAName(e.target.value)}
              placeholder="例如：張三"
            />
          </Grid>

          {/* 性別 */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>性別</InputLabel>
              <Select
                value={personAGender}
                label="性別"
                onChange={(e: SelectChangeEvent) => setPersonAGender(e.target.value as 'Male' | 'Female')}
              >
                <MenuItem value="Male">男</MenuItem>
                <MenuItem value="Female">女</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* 出生年 */}
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="出生年"
              type="number"
              value={personAYear}
              onChange={(e) => setPersonAYear(e.target.value)}
              placeholder="1995"
              inputProps={{ min: 1900, max: 2100 }}
            />
          </Grid>

          {/* 出生月 */}
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="出生月"
              type="text"
              value={personAMonth}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                setPersonAMonth(value);
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value && value.length === 1) {
                  setPersonAMonth(value.padStart(2, '0'));
                }
              }}
              placeholder="03"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          {/* 出生日 */}
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="出生日"
              type="text"
              value={personADay}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                setPersonADay(value);
              }}
              onBlur={(e) => {
                const value = e.target.value;
                if (value && value.length === 1) {
                  setPersonADay(value.padStart(2, '0'));
                }
              }}
              placeholder="21"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          {/* 出生時辰 */}
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth>
              <InputLabel>出生時辰</InputLabel>
              <Select
                value={personAHour}
                label="出生時辰"
                onChange={(e: SelectChangeEvent) => setPersonAHour(e.target.value)}
              >
                {hourOptions && Array.isArray(hourOptions) && hourOptions.length > 0 && hourOptions
                  .filter(option => option && typeof option === 'object' && option.value && option.label)
                  .map((option) => (
                    <MenuItem key={String(option.value)} value={String(option.value)}>
                      {String(option.label)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Person B 資料輸入（單人配對模式）*/}
      {isSinglePairType && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            對方資料（Person B）
          </Typography>
          {personBFromShare ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              已從分享命盤預填「{personBFromShare}」的資料，請確認後按「開始配對分析」
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              請輸入您想要配對的對象資料
            </Alert>
          )}
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* 姓名（可選）*/}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="姓名（可選）"
                value={personBName}
                onChange={(e) => setPersonBName(e.target.value)}
                placeholder="例如：李四"
              />
            </Grid>

            {/* 性別 */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>性別</InputLabel>
                <Select
                  value={personBGender}
                  label="性別"
                  onChange={(e: SelectChangeEvent) => setPersonBGender(e.target.value as 'Male' | 'Female')}
                >
                  <MenuItem value="Male">男</MenuItem>
                  <MenuItem value="Female">女</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* 出生年 */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="出生年"
                type="number"
                value={personBYear}
                onChange={(e) => setPersonBYear(e.target.value)}
                placeholder="1996"
                inputProps={{ min: 1900, max: 2100 }}
              />
            </Grid>

            {/* 出生月 */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="出生月"
                type="text"
                value={personBMonth}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                  setPersonBMonth(value);
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value && value.length === 1) {
                    setPersonBMonth(value.padStart(2, '0'));
                  }
                }}
                placeholder="05"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* 出生日 */}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="出生日"
                type="text"
                value={personBDay}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                  setPersonBDay(value);
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (value && value.length === 1) {
                    setPersonBDay(value.padStart(2, '0'));
                  }
                }}
                placeholder="15"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* 出生時辰 */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>出生時辰</InputLabel>
                <Select
                  value={personBHour}
                  label="出生時辰"
                  onChange={(e: SelectChangeEvent) => setPersonBHour(e.target.value)}
                >
                  {hourOptions && Array.isArray(hourOptions) && hourOptions.length > 0 && hourOptions
                    .filter(option => option && typeof option === 'object' && option.value && option.label)
                    .map((option) => (
                      <MenuItem key={String(option.value)} value={String(option.value)}>
                        {String(option.label)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 多人資料輸入（工作夥伴模式）*/}
      {isMultiPairType && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            適配分析：您可以輸入最多 5 位配對對象的資料，系統將分析您與每位的適配度
          </Alert>

          {multiplePersons && Array.isArray(multiplePersons) && multiplePersons.map((person, index) => (
            <Paper key={person.id} elevation={2} sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  夥伴 {index + 1}
                </Typography>
                {multiplePersons.length > 1 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleRemovePerson(person.id)}
                  >
                    移除
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* 姓名（可選）*/}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="姓名（可選）"
                    value={person.name}
                    onChange={(e) => handleUpdatePerson(person.id, 'name', e.target.value)}
                    placeholder={`例如：夥伴${index + 1}`}
                  />
                </Grid>

                {/* 性別 */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>性別</InputLabel>
                    <Select
                      value={person.gender}
                      label="性別"
                      onChange={(e: SelectChangeEvent) => handleUpdatePerson(person.id, 'gender', e.target.value as 'Male' | 'Female')}
                    >
                      <MenuItem value="Male">男</MenuItem>
                      <MenuItem value="Female">女</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* 出生年 */}
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="出生年"
                    type="number"
                    value={person.year}
                    onChange={(e) => handleUpdatePerson(person.id, 'year', e.target.value)}
                    placeholder="1996"
                    inputProps={{ min: 1900, max: 2100 }}
                  />
                </Grid>

                {/* 出生月 */}
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="出生月"
                    type="text"
                    value={person.month}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                      handleUpdatePerson(person.id, 'month', value);
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && value.length === 1) {
                        handleUpdatePerson(person.id, 'month', value.padStart(2, '0'));
                      }
                    }}
                    placeholder="05"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>

                {/* 出生日 */}
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="出生日"
                    type="text"
                    value={person.day}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                      handleUpdatePerson(person.id, 'day', value);
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && value.length === 1) {
                        handleUpdatePerson(person.id, 'day', value.padStart(2, '0'));
                      }
                    }}
                    placeholder="15"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>

                {/* 出生時辰 */}
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>出生時辰</InputLabel>
                    <Select
                      value={person.hour}
                      label="出生時辰"
                      onChange={(e: SelectChangeEvent) => handleUpdatePerson(person.id, 'hour', e.target.value)}
                    >
                      {hourOptions && Array.isArray(hourOptions) && hourOptions.length > 0 && hourOptions
                        .filter(option => option && typeof option === 'object' && option.value && option.label)
                        .map((option) => (
                          <MenuItem key={String(option.value)} value={String(option.value)}>
                            {String(option.label)}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {/* Add Person Button */}
          {multiplePersons.length < 5 && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleAddPerson}
                startIcon={<PeopleIcon />}
              >
                新增夥伴 ({multiplePersons.length}/5)
              </Button>
            </Box>
          )}
        </>
      )}

      {/* 提交按鈕 */}
      <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              py: 1.5,
              px: 6,
              fontSize: '1.1rem',
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '開始配對分析'}
          </Button>
          <Typography variant="caption" sx={{ color: shareToken ? 'success.main' : 'text.secondary' }}>
            {shareToken ? '本次配對分析免費（由分享命盤提供）' : `點擊後將扣除 ${String(formatPrice(pairCost))}`}
          </Typography>

          {/* 邀請連結按鈕（僅雙人配對問題顯示） */}
          {isSinglePairType && (
            <>
              <Divider sx={{ width: '100%' }}>或</Divider>
              <Button
                variant="contained"
                size="large"
                startIcon={inviteLoading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                onClick={handleCreateInvite}
                disabled={inviteLoading}
                sx={{
                  background: 'linear-gradient(135deg, #11998e, #38ef7d)',
                  color: 'white',
                  py: 1.5,
                  px: 4,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(17,153,142,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d8a7e, #2dd66b)',
                    boxShadow: '0 6px 20px rgba(17,153,142,0.5)',
                  },
                }}
              >
                產生邀請連結，讓對方自行填寫自己的生辰資料
              </Button>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
                對方點擊連結後填寫出生資料，系統自動完成配對分析（連結 24 小時內有效）
              </Typography>
              {inviteError && (
                <Alert severity="error" sx={{ width: '100%', mt: 0 }}>{inviteError}</Alert>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* 確認對話框 */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelAnalysis}>
        <DialogTitle>確認配對分析</DialogTitle>
        <DialogContent>
          {/* Person A 資訊 */}
          <DialogContentText>
            <strong>Person A (您):</strong> {String(personAName || '未填寫')} ({personAGender === 'Male' ? '男' : '女'})
            <br />
            出生時間: {String(personAYear)}/{String(personAMonth)}/{String(personADay)} {String(hourOptions.find(h => h.value === personAHour)?.label || '')}
          </DialogContentText>

          {/* 單人配對模式 */}
          {isSinglePairType && (
            <DialogContentText sx={{ mt: 2 }}>
              <strong>Person B:</strong> {String(personBName || '未填寫')} ({personBGender === 'Male' ? '男' : '女'})
              <br />
              出生時間: {String(personBYear)}/{String(personBMonth)}/{String(personBDay)} {String(hourOptions.find(h => h.value === personBHour)?.label || '')}
            </DialogContentText>
          )}

          {/* 多人配對模式 */}
          {isMultiPairType && multiplePersons && Array.isArray(multiplePersons) && (
            <Box sx={{ mt: 2 }}>
              <DialogContentText>
                <strong>配對對象 ({multiplePersons.length} 位):</strong>
              </DialogContentText>
              {multiplePersons.map((person, index) => (
                <DialogContentText key={person.id} sx={{ mt: 1, ml: 2 }}>
                  {index + 1}. {String(person.name || '未填寫')} ({person.gender === 'Male' ? '男' : '女'})
                  <br />
                  {String(person.year)}/{String(person.month)}/{String(person.day)} {String(hourOptions.find(h => h.value === person.hour)?.label || '')}
                </DialogContentText>
              ))}
            </Box>
          )}

          {/* 關係類型 */}
          <DialogContentText sx={{ mt: 2 }}>
            <strong>關係類型:</strong> {String(relationshipTypeOptions.find(r => r.value === relationshipType)?.label || '')}
          </DialogContentText>

          {/* 費用提示 */}
          {isSinglePairType && (
            <>
              {shareToken ? (
                <DialogContentText sx={{ mt: 2, color: 'success.main' }}>
                  本次配對分析<strong>免費</strong>（由分享命盤提供），確定要繼續嗎？
                </DialogContentText>
              ) : (
                <>
                  <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
                    此次配對分析將扣除 <strong>{String(formatPrice(pairCost))}</strong>，確定要繼續嗎？
                  </DialogContentText>
                  {user && (
                    <DialogContentText sx={{ mt: 1 }}>
                      當前餘額: {String(formatPrice(user.balance || 0))} → 查詢後餘額: {String(formatPrice((user.balance || 0) - pairCost))}
                    </DialogContentText>
                  )}
                </>
              )}
            </>
          )}

          {/* 多人配對費用提示 */}
          {isMultiPairType && multiplePersons && (
            <>
              <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
                適配分析：將與 <strong>{multiplePersons.length}</strong> 位配對對象進行分析
              </DialogContentText>
              <DialogContentText sx={{ color: 'error.main' }}>
                此次配對分析將扣除 <strong>{String(formatPrice(pairCost))}</strong>（以次計費，不以人數計費），確定要繼續嗎？
              </DialogContentText>
              {user && (
                <DialogContentText sx={{ mt: 1 }}>
                  當前餘額: {String(formatPrice(user.balance || 0))} → 查詢後餘額: {String(formatPrice((user.balance || 0) - pairCost))}
                </DialogContentText>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAnalysis} color="inherit">
            取消
          </Button>
          <Button onClick={handleConfirmAnalysis} variant="contained" color="primary" autoFocus>
            確定配對
          </Button>
        </DialogActions>
      </Dialog>

      {/* 邀請連結對話框 */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>邀請連結已產生</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            將以下連結分享給對方，對方填寫出生資料後，系統將自動完成配對分析。連結有效期 24 小時。
          </Typography>
          <TextField
            fullWidth
            value={inviteUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(inviteUrl);
                      setCopySnackbar(true);
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>關閉</Button>
          <Button
            variant="contained"
            onClick={() => {
              navigator.clipboard.writeText(inviteUrl);
              setCopySnackbar(true);
            }}
            startIcon={<ContentCopyIcon />}
          >
            複製連結
          </Button>
        </DialogActions>
      </Dialog>

      {/* 複製成功提示 */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="連結已複製"
      />
    </Container>
  );
};

export default PairAnalysisPage;
